import React from "react";
import { bookendTextDefaults, findBookendFont, nearestWeight, type CardElement, type TextStyle } from "@veyso/core";

/**
 * The controls that belong on the frame rather than in a panel.
 *
 * Everything here is something you judge by looking at the card: whether the
 * line is bold enough, whether it is big enough, which edge it sits against.
 * Choosing a typeface or setting an exit is a decision you make once and it
 * stays in the properties pane, where there is room to explain it.
 *
 * It floats above the selected element and follows it, so the thing being
 * changed and the control changing it are never on opposite sides of the
 * screen.
 */
export interface CardToolbarProps {
  element: CardElement;
  onPatch: (patch: Record<string, unknown>) => void;
  onPatchStyle: (patch: Partial<TextStyle>) => void;
  onDelete: () => void;
}

const Button: React.FC<{
  title: string;
  on?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ title, on = false, disabled = false, onClick, children }) => (
  <button
    title={title}
    aria-label={title}
    aria-pressed={on}
    data-selected={on}
    disabled={disabled}
    onPointerDown={(event) => event.stopPropagation()}
    onClick={onClick}
    className="flex h-[26px] min-w-[26px] items-center justify-center rounded-[4px] px-1.5 text-[12px] text-ink-300 transition-colors hover:bg-bay-750 hover:text-ink-100 disabled:opacity-30 data-[selected=true]:bg-ember-500/25 data-[selected=true]:text-ink-100"
  >
    {children}
  </button>
);

const Divider: React.FC = () => <span className="mx-0.5 h-[16px] w-px bg-[var(--edge-strong)]" />;

/**
 * Alignment, drawn rather than typed.
 *
 * These were `⯇` and `⯈` — U+2BC7 and U+2BC8, from Miscellaneous Symbols and
 * Arrows, which almost no interface font carries. They rendered as the browser's
 * missing-glyph box, so two of the three buttons showed a question mark while
 * the middle one worked, because `≡` happens to be common. A character that
 * depends on the font having heard of it is not an icon.
 *
 * Four lines at the widths real ragged text makes, pinned to the edge the button
 * chooses — which is also what the button does.
 */
const AlignIcon: React.FC<{ side: "left" | "center" | "right" }> = ({ side }) => {
  const widths = [12, 7, 10, 5];
  const x = (w: number) => (side === "left" ? 1 : side === "right" ? 13 - w : (14 - w) / 2);
  return (
    <svg width="14" height="12" viewBox="0 0 14 12" aria-hidden="true" fill="currentColor">
      {widths.map((w, i) => (
        <rect key={i} x={x(w)} y={i * 3 + 0.5} width={w} height="1.5" rx="0.75" />
      ))}
    </svg>
  );
};

export const CardToolbar: React.FC<CardToolbarProps> = ({ element, onPatch, onPatchStyle, onDelete }) => {
  const text = element.kind === "text" ? element : null;
  const fallback = text ? bookendTextDefaults[text.role] : null;
  const style = text?.style;

  const size = style?.fontSize ?? fallback?.fontSize ?? 88;
  const bold = style?.bold ?? fallback?.bold ?? false;
  const italic = style?.italic ?? fallback?.italic ?? false;
  const strike = style?.strikethrough ?? fallback?.strikethrough ?? false;
  const colour = style?.color ?? fallback?.color ?? "#f8fafc";

  // The weight the card will really be drawn at, so a display face that only
  // carries 400 does not light the B button up as though it did something.
  const family = text ? findBookendFont(style?.fontFamily ?? fallback?.fontFamily ?? "") : undefined;
  const boldIsReal =
    !family ||
    nearestWeight(family.weights, fallback?.weights.bold ?? 800) !==
      nearestWeight(family.weights, fallback?.weights.normal ?? 500);

  return (
    <div
      onPointerDown={(event) => event.stopPropagation()}
      className="pointer-events-auto flex items-center gap-0.5 rounded-[7px] border border-[var(--edge-strong)] bg-bay-850/95 px-1 py-1 shadow-lg backdrop-blur"
    >
      {text ? (
        <>
          <Button title="Smaller" onClick={() => onPatchStyle({ fontSize: Math.max(8, size - 4) })}>
            −
          </Button>
          <span className="w-[30px] text-center text-[11px] tabular-nums text-ink-300">{Math.round(size)}</span>
          <Button title="Bigger" onClick={() => onPatchStyle({ fontSize: Math.min(300, size + 4) })}>
            +
          </Button>
          <Divider />
          <Button
            title={boldIsReal ? "Bold" : "Bold — this face has one weight"}
            on={bold}
            disabled={!boldIsReal}
            onClick={() => onPatchStyle({ bold: !bold })}
          >
            <span className="font-bold">B</span>
          </Button>
          <Button title="Italic" on={italic} onClick={() => onPatchStyle({ italic: !italic })}>
            <span className="font-serif italic">I</span>
          </Button>
          <Button title="Strikethrough" on={strike} onClick={() => onPatchStyle({ strikethrough: !strike })}>
            <span className="line-through">S</span>
          </Button>
          <Divider />
          {(["left", "center", "right"] as const).map((align) => (
            <Button key={align} title={`Align ${align}`} on={text.align === align} onClick={() => onPatch({ align })}>
              <AlignIcon side={align} />
            </Button>
          ))}
          <Divider />
          <label
            title="Text colour"
            className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[4px] transition-colors hover:bg-bay-750"
          >
            <span
              className="h-[14px] w-[14px] rounded-[3px] border border-[var(--edge-strong)]"
              style={{ background: colour }}
            />
            <input
              type="color"
              className="sr-only"
              value={/^#[0-9a-f]{6}$/i.test(colour) ? colour : "#f8fafc"}
              onChange={(event) => onPatchStyle({ color: event.target.value })}
            />
          </label>
        </>
      ) : (
        <span className="px-1.5 text-[11px] capitalize text-ink-400">
          {element.kind === "shape" ? element.shape : "Image"}
        </span>
      )}

      <Divider />
      <Button title="Delete" onClick={onDelete}>
        <span className="text-ember-400">×</span>
      </Button>
    </div>
  );
};
